import React from 'react';

interface Props {
  className?: string;
  color?: string;
}

/**
 * A multi-layered industrial skyline silhouette drawn as continuous paths.
 * Features cranes, cooling towers, smokestacks, refineries, and pipelines
 * all flowing together as one cohesive panoramic skyline.
 */
export const IndustrialSkyline: React.FC<Props> = ({
  className = '',
  color = 'currentColor',
}) => (
  <svg
    viewBox="0 0 1440 320"
    className={className}
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="skyFade" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0" />
        <stop offset="40%" stopColor={color} stopOpacity="0.03" />
        <stop offset="100%" stopColor={color} stopOpacity="0.12" />
      </linearGradient>
      <linearGradient id="glowUp" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor={color} stopOpacity="0.08" />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </linearGradient>
    </defs>

    {/* Atmospheric gradient base */}
    <rect x="0" y="160" width="1440" height="160" fill="url(#skyFade)" />

    {/* === BACK LAYER — distant, lighter === */}
    <path
      d={`
        M0 320
        L0 280
        L30 280 L30 240 L38 240 L38 200 L46 200 L46 240 L54 240 L54 280
        L90 280 L90 260 L100 260 L100 250 L110 250 L110 260 L120 260 L120 280
        L180 280 L180 230 Q195 200 210 230 L210 280
        L260 280 L260 250 L300 250 L300 240 L310 240 L310 250 L340 250 L340 280
        L400 280 L400 260 L410 260 L410 230 L418 230 L418 180 L422 180 L422 230 L430 230 L430 260 L440 260 L440 280
        L500 280 L500 270 L520 270 L520 250 L540 250 L540 270 L560 270 L560 280
        L620 280 L620 240 Q632 210 644 240 L644 280
        L700 280 L700 265 L720 265 L720 255 L740 255 L740 265 L760 265 L760 280
        L830 280 L830 250 L838 250 L838 190 L842 190 L842 250 L850 250 L850 280
        L900 280 L900 260 L920 260 L920 250 L960 250 L960 260 L980 260 L980 280
        L1040 280 L1040 240 Q1055 215 1070 240 L1070 280
        L1120 280 L1120 270 L1140 270 L1140 255 L1150 255 L1150 270 L1170 270 L1170 280
        L1220 280 L1220 250 L1228 250 L1228 210 L1232 210 L1232 250 L1240 250 L1240 280
        L1300 280 L1300 260 L1310 260 L1310 245 L1330 245 L1330 260 L1340 260 L1340 280
        L1400 280 L1400 265 L1420 265 L1420 245 L1430 245 L1430 265 L1440 265 L1440 320 Z
      `}
      fill={color}
      opacity="0.04"
    />

    {/* === MIDDLE LAYER — main skyline === */}
    <path
      d={`
        M0 320
        L0 295

        L20 295 L20 290 L25 290 L25 250 L27 250 L27 180 L31 175 L35 180 L35 250 L37 250 L37 290 L42 290 L42 295

        L70 295 L70 288 L80 288 L80 270 L85 270 L85 245
        L85 150 L87 148 L125 148 L127 150
        L127 155 L112 155 L112 148
        L127 245 L127 270 L132 270 L132 288 L142 288 L142 295

        L170 295 L170 285 L180 285 L180 250 Q198 218 216 250 L216 285 L226 285 L226 295

        L260 295 L260 288 L270 288 L270 265 L275 265 L275 248 L280 248 L280 230 L330 230 L330 248 L335 248 L335 265 L340 265 L340 288 L350 288 L350 295

        L380 295 L380 290 L386 290 L386 240 L388 240 L388 180 L390 176 L392 180 L392 240 L394 240 L394 290 L400 290 L400 295

        L430 295 L430 288 L440 288 L440 260 L445 260 L445 240 L500 240 L500 260 L505 260 L505 288 L515 288 L515 295

        L545 295 L545 290 L555 290 L555 265 L560 250 L564 180 L568 120 L570 118 L572 120 L576 180 L580 250 L585 265 L595 290 L605 290 L605 295

        L635 295 L635 288 L645 288 L645 265 L650 265 L650 250
        Q668 220 686 250
        L686 265 L691 265 L691 288 L701 288 L701 295

        L730 295 L730 290 L738 290 L738 258 L740 258 L740 200 L742 196 L744 200 L744 258 L746 258 L746 290 L754 290 L754 295

        L790 295 L790 285 L800 285 L800 262 L810 262 L810 245 L860 245 L860 262 L870 262 L870 285 L880 285 L880 295

        L910 295 L910 288 L920 288 L920 265 L930 265 L930 250 Q945 222 960 250 L960 265 L970 265 L970 288 L980 288 L980 295

        L1010 295 L1010 290 L1015 290 L1015 240 L1017 240 L1017 190 L1019 186 L1021 190 L1021 240 L1023 240 L1023 290 L1028 290 L1028 295

        L1060 295 L1060 288 L1070 288 L1070 262 L1076 262 L1076 248 L1130 248 L1130 262 L1136 262 L1136 288 L1146 288 L1146 295

        L1175 295 L1175 285 L1185 285 L1185 260 L1192 260 L1192 245 Q1208 215 1224 245 L1224 260 L1231 260 L1231 285 L1241 285 L1241 295

        L1270 295 L1270 290 L1278 290 L1278 255 L1280 255 L1280 195 L1282 190 L1284 195 L1284 255 L1286 255 L1286 290 L1294 290 L1294 295

        L1320 295 L1320 288 L1330 288 L1330 262 L1340 262 L1340 248 L1380 248 L1380 262 L1390 262 L1390 288 L1400 288 L1400 295

        L1420 295 L1420 280 L1430 280 L1430 265 L1435 265 L1435 280 L1440 280 L1440 320 Z
      `}
      fill={color}
      opacity="0.07"
    />

    {/* === FRONT LAYER — closest, darkest === */}
    <path
      d={`
        M0 320
        L0 304

        L50 304 L50 298 L60 298 L60 285 L65 285 L65 275 L100 275 L100 285 L105 285 L105 298 L115 298 L115 304

        L200 304 L200 300 L205 300 L205 288 L210 288 L210 300 L215 300 L215 304

        L280 304 L280 298 L290 298 L290 282 L296 282 L296 270 L340 270 L340 282 L346 282 L346 298 L356 298 L356 304

        L440 304 L440 300 L446 300 L446 290 L450 290 L450 300 L456 300 L456 304

        L530 304 L530 298 L540 298 L540 280 L548 280 L548 268 L590 268 L590 280 L598 280 L598 298 L608 298 L608 304

        L680 304 L680 300 L685 300 L685 290 L688 290 L688 300 L693 300 L693 304

        L760 304 L760 298 L770 298 L770 280 L780 280 L780 270 L820 270 L820 280 L830 280 L830 298 L840 298 L840 304

        L920 304 L920 300 L926 300 L926 288 L930 288 L930 300 L936 300 L936 304

        L1000 304 L1000 298 L1010 298 L1010 278 L1020 278 L1020 268 L1060 268 L1060 278 L1070 278 L1070 298 L1080 298 L1080 304

        L1150 304 L1150 300 L1156 300 L1156 292 L1160 292 L1160 300 L1166 300 L1166 304

        L1240 304 L1240 298 L1250 298 L1250 282 L1258 282 L1258 272 L1298 272 L1298 282 L1306 282 L1306 298 L1316 298 L1316 304

        L1380 304 L1380 300 L1385 300 L1385 290 L1388 290 L1388 300 L1393 300 L1393 304

        L1440 304 L1440 320 Z
      `}
      fill={color}
      opacity="0.1"
    />

    {/* Subtle bottom glow strip */}
    <rect x="0" y="305" width="1440" height="15" fill="url(#glowUp)" />
  </svg>
);

/**
 * Compact version for smaller areas like the sidebar.
 */
export const IndustrialAccent: React.FC<Props> = ({
  className = '',
  color = 'currentColor',
}) => (
  <svg
    viewBox="0 0 240 60"
    className={className}
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
    fill={color}
  >
    {/* Back layer */}
    <path
      d="M0 60 L0 50 L8 50 L8 30 L10 28 L12 30 L12 50 L20 50 L20 42 Q28 35 36 42 L36 50
         L50 50 L50 38 L55 38 L55 28 L75 28 L75 38 L80 38 L80 50
         L95 50 L95 45 L97 45 L97 25 L99 22 L101 25 L101 45 L103 45 L103 50
         L118 50 L118 40 Q126 32 134 40 L134 50
         L148 50 L148 42 L153 42 L153 32 L170 32 L170 42 L175 42 L175 50
         L190 50 L190 45 L192 45 L192 28 L194 25 L196 28 L196 45 L198 45 L198 50
         L210 50 L210 40 L215 40 L215 35 L225 35 L225 40 L230 40 L230 50
         L240 50 L240 60 Z"
      opacity="0.12"
    />
    {/* Front layer */}
    <path
      d="M0 60 L0 54 L15 54 L15 48 L20 48 L20 44 L40 44 L40 48 L45 48 L45 54
         L70 54 L70 52 L73 52 L73 48 L76 48 L76 52 L79 52 L79 54
         L105 54 L105 48 L112 48 L112 44 L130 44 L130 48 L137 48 L137 54
         L160 54 L160 52 L163 52 L163 46 L166 46 L166 52 L169 52 L169 54
         L195 54 L195 48 L200 48 L200 44 L220 44 L220 48 L225 48 L225 54
         L240 54 L240 60 Z"
      opacity="0.18"
    />
  </svg>
);

export default IndustrialSkyline;
